
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { UserPlus, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { createFollowNotification } from '@/utils/notification-utils';

interface FollowBackSuggestionProps {
  userId: string;
  userName: string;
  userAvatar?: string;
  onDismiss: () => void;
}

const FollowBackSuggestion: React.FC<FollowBackSuggestionProps> = ({
  userId,
  userName,
  userAvatar,
  onDismiss
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleFollowBack = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('user_follows')
        .insert({ follower_id: user.id, following_id: userId });
        
      if (error) throw error;
      
      // Create notification
      const { data: currentUserProfile } = await supabase
        .from('profiles')
        .select('full_name, username')
        .eq('id', user.id)
        .single();
        
      const followerName = currentUserProfile?.full_name || currentUserProfile?.username || 'Someone';
      await createFollowNotification(userId, followerName);
      
      toast({
        title: "Following back!",
        description: `You are now following ${userName}`
      });
      
      onDismiss();
    } catch (error) {
      console.error('Error following back:', error);
      toast({
        title: "Error",
        description: "Could not follow back",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-4 mb-4 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={userAvatar} />
            <AvatarFallback>{userName.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{userName}</p>
            <p className="text-sm text-muted-foreground">Follows you • Follow back?</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            onClick={handleFollowBack}
            disabled={loading}
            className="bg-primary hover:bg-primary/90"
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Follow Back
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={onDismiss}
            className="hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default FollowBackSuggestion;
